import {
  Badge,
  Box,
  Button,
  HiddenVisually,
  IconInfo,
  IconTick,
  Inline,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import type { ReactElement, ReactNode } from 'react';

import * as styles from './DesktopPackageColumns.css';

interface DesktopPackageColumnsProps {
  onFreemiumClick: () => void;
  onStandardClick: () => void;
  onAccelerateClick: () => void;
  htags: {
    freemium: string;
    standard: string;
    accelerate: string;
  };
}

export const DesktopPackageColumns = (props: DesktopPackageColumnsProps) => (
  <Box
    component="table"
    width="full"
    className={styles.table}
    aria-label="Compare selling approaches"
  >
    <Box component="thead">
      <Box component="tr" className={styles.row}>
        <Box
          component="th"
          scope="col"
          paddingX="small"
          paddingTop="small"
          paddingBottom="large"
          textAlign="left"
          className={[styles.cell, styles.alignY.bottom, styles.featureColumn]}
        >
          <Text size="large" weight="strong">
            Feature
          </Text>
        </Box>

        <Box
          component="th"
          scope="col"
          paddingX="small"
          paddingTop="small"
          paddingBottom="large"
          className={[styles.cell, styles.alignY.bottom]}
          data={{ testid: 'desktop-package-header-cell-freemium' }}
        >
          <PackageHeading name="Free" />
        </Box>

        <Box
          component="th"
          scope="col"
          paddingX="small"
          paddingTop="small"
          paddingBottom="large"
          className={[styles.cell, styles.alignY.bottom]}
          data={{ testid: 'desktop-package-header-cell-standard' }}
        >
          <PackageHeading name="Standard" />
        </Box>

        <Box
          component="th"
          scope="col"
          paddingX="small"
          paddingTop="small"
          paddingBottom="large"
          className={[styles.cell, styles.alignY.bottom]}
          data={{ testid: 'desktop-package-header-cell-accelerate' }}
        >
          <PackageHeading name="Accelerate" isBestPerforming />
        </Box>
      </Box>
    </Box>

    <Box component="tbody" className={styles.section}>
      <FeatureRow
        title="Listing duration"
        description="How long your business stays on-site"
        free={<TextCell>6 months</TextCell>}
        standard={<TextCell>Until sold</TextCell>}
        accelerate={<TextCell>Until sold</TextCell>}
      />

      <FeatureRow
        title="Search appearances"
        description="How often your business appears in search results compared to Free"
        tooltipMessage={TOOLTIP_MESSAGE}
        free={<TextCell>1x</TextCell>}
        standard={<TextCell>4.4x</TextCell>}
        accelerate={<TextCell>6.8x</TextCell>}
      />

      <FeatureRow
        title="Views by potential buyers"
        description="How often your business gets viewed compared to Free"
        tooltipMessage={TOOLTIP_MESSAGE}
        free={<TextCell>1x</TextCell>}
        standard={<TextCell>2.2x</TextCell>}
        accelerate={<TextCell>4.5x</TextCell>}
      />

      <FeatureRow
        title="Photo shown in search results"
        description="Buyers see a preview photo from your listing when searching on-site"
        free={<UpgradeCell />}
        standard={<TickCell />}
        accelerate={<TickCell />}
      />

      <FeatureRow
        title="Reach buyers off-site"
        description="Your business is recommended to relevant buyers via email"
        free={<UpgradeCell />}
        standard={<TickCell />}
        accelerate={<TickCell />}
      />

      <FeatureRow
        title="Promoted placement on-site"
        description="Your business is featured at the top of search results"
        free={<UpgradeCell />}
        standard={<UpgradeCell />}
        accelerate={<TickCell />}
      />

      <FeatureRow
        title="Promoted placement off-site"
        description="Your business gets featured placement in buyer recommendation emails"
        free={<UpgradeCell />}
        standard={<UpgradeCell />}
        accelerate={<TickCell />}
      />
    </Box>

    <Box component="tfoot" className={styles.section}>
      <Box component="tr" className={styles.row}>
        <Box
          component="td"
          padding="small"
          className={[styles.cell, styles.alignY.top]}
        >
          <Box paddingY="small">
            <Text size="standard" weight="strong">
              Price
            </Text>
          </Box>
        </Box>

        <Box
          component="td"
          padding="small"
          className={[styles.cell, styles.alignY.top]}
        >
          <PriceWithChoose
            price="$0"
            packageName="Free"
            htag={props.htags.freemium}
            onClick={props.onFreemiumClick}
          />
        </Box>

        <Box
          component="td"
          padding="small"
          className={[styles.cell, styles.alignY.top]}
        >
          <PriceWithChoose
            price="$949"
            hasGst
            packageName="Standard"
            htag={props.htags.standard}
            onClick={props.onStandardClick}
          />
        </Box>

        <Box
          component="td"
          padding="small"
          className={[styles.cell, styles.alignY.top]}
        >
          <PriceWithChoose
            price="$1299"
            hasGst
            packageName="Accelerate"
            htag={props.htags.accelerate}
            onClick={props.onAccelerateClick}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

const TOOLTIP_MESSAGE =
  'Numbers are based on the average performance of paid listings compared to Free listings between July 2025 – June 2026.';

interface PackageHeadingProps {
  name: string;
  isBestPerforming?: boolean;
}

const PackageHeading = (props: PackageHeadingProps) => (
  <Stack space="medium" align="center">
    {props.isBestPerforming ? (
      <Badge tone="promote" bleedY>
        Best performing
      </Badge>
    ) : null}

    <Text size="large" weight="strong">
      {props.name}
    </Text>
  </Stack>
);

interface PriceWithChooseProps {
  price: string;
  hasGst?: boolean;
  packageName: string;
  htag: string;
  onClick: () => void;
}

const PriceWithChoose = (props: PriceWithChooseProps) => (
  <Box paddingY="small">
    <Stack space="xlarge" align="center">
      <Inline
        space="xxsmall"
        align="center"
        alignY="center"
        data={{ testid: 'package-price' }}
      >
        <Text size="large" weight="regular">
          {props.price}
        </Text>
        {props.hasGst ? (
          <Text size="xsmall" weight="regular" tone="secondary">
            + GST
          </Text>
        ) : null}
      </Inline>
      <Box className={styles.chooseButton}>
        <Button
          tone="formAccent"
          onClick={props.onClick}
          aria-label={`Choose ${props.packageName}`}
          data={{ htag: props.htag }}
        >
          Choose
        </Button>
      </Box>
    </Stack>
  </Box>
);

interface FeatureRowProps {
  title: string;
  description: string;
  tooltipMessage?: string;
  free: ReactElement;
  standard: ReactElement;
  accelerate: ReactElement;
}

const FeatureRow = (props: FeatureRowProps) => (
  <Box component="tr" className={styles.row}>
    <Box
      component="td"
      padding="small"
      className={[styles.cell, styles.alignY.middle]}
    >
      <RowHeading
        title={props.title}
        description={props.description}
        tooltipMessage={props.tooltipMessage}
      />
    </Box>
    <Box
      component="td"
      padding="small"
      className={[styles.cell, styles.alignY.middle]}
    >
      <Inline space="none" align="center">
        {props.free}
      </Inline>
    </Box>
    <Box
      component="td"
      padding="small"
      className={[styles.cell, styles.alignY.middle]}
    >
      <Inline space="none" align="center">
        {props.standard}
      </Inline>
    </Box>
    <Box
      component="td"
      padding="small"
      className={[styles.cell, styles.alignY.middle]}
    >
      <Inline space="none" align="center">
        {props.accelerate}
      </Inline>
    </Box>
  </Box>
);

interface RowHeadingProps {
  title: string;
  description: string;
  tooltipMessage?: string;
}

const RowHeading = (props: RowHeadingProps) => (
  <Box paddingY="small">
    <Stack space="xsmall">
      <Inline space="xsmall" alignY="center">
        <Text size="standard" weight="strong">
          {props.title}
        </Text>
        {props.tooltipMessage ? (
          <TooltipRenderer
            tooltip={<Text size="small">{props.tooltipMessage}</Text>}
          >
            {({ triggerProps }) => (
              <Box {...triggerProps} display="flex" alignItems="center">
                <IconInfo size="xsmall" tone="formAccent" />
              </Box>
            )}
          </TooltipRenderer>
        ) : null}
      </Inline>
      <Text size="small">{props.description}</Text>
    </Stack>
  </Box>
);

interface TextCellProps {
  children: ReactNode;
}

const TextCell = (props: TextCellProps) => (
  <Text size="small">{props.children}</Text>
);

const TickCell = () => (
  <>
    <IconTick tone="neutral" size="large" />
    <HiddenVisually>Included</HiddenVisually>
  </>
);

const UpgradeCell = () => <Text size="small">Upgrade</Text>;
